import React, { useState, useEffect } from 'react';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlatformService from '../services/PlatformService';

const recentActivity = [
  { id: 1, user: 'Marie Dupont', action: 'a créé une nouvelle boutique', time: 'Il y a 2 min', type: 'shop' },
  { id: 2, user: 'Jean Martin', action: "s'est inscrit", time: 'Il y a 5 min', type: 'user' },
  { id: 3, user: 'Sophie Bernard', action: 'a modifié ses permissions', time: 'Il y a 12 min', type: 'security' },
  { id: 4, user: 'Pierre Durand', action: 'a publié 15 produits', time: 'Il y a 25 min', type: 'product' },
  { id: 5, user: 'Admin System', action: 'a effectué une sauvegarde', time: 'Il y a 1h', type: 'system' },
];

const topShops = [
  { id: 1, name: 'Fashion Store', owner: 'Marie D.', products: 234, revenue: '€12,450' },
  { id: 2, name: 'Tech Galaxy', owner: 'Jean M.', products: 189, revenue: '€9,870' },
  { id: 3, name: 'Home & Deco', owner: 'Sophie B.', products: 156, revenue: '€7,230' },
  { id: 4, name: 'Sport Zone', owner: 'Pierre D.', products: 142, revenue: '€5,890' },
];

const StatCard = ({ stat, loading }) => {
  const Icon = stat.icon;
  const colorClasses = {
    accent: 'from-accent-500 to-accent-700',
    success: 'from-emerald-500 to-emerald-600',
    info: 'from-sky-500 to-sky-600',
    warning: 'from-amber-500 to-amber-600',
  };

  return (
    <div className="stat-card group animate-in">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[stat.color]} shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <button className="p-1.5 text-surface-500 hover:text-surface-300 hover:bg-surface-800 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
          <MoreHorizIcon className="w-5 h-5" />
        </button>
      </div>
      
      <div className="mt-4">
        {loading ? (
          <div className="h-8 w-20 bg-surface-700 rounded animate-pulse" />
        ) : (
          <p className="stat-value">{stat.value}</p>
        )}
        <p className="stat-label">{stat.name}</p>
      </div>
      
      {stat.subtitle && (
        <p className="text-xs text-surface-500 mt-2">{stat.subtitle}</p>
      )}
    </div>
  );
};

const SystemStatusBadge = ({ status }) => {
  const isOperational = status === 'operational';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      isOperational 
        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
        : 'bg-red-500/10 text-red-400 border border-red-500/20'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isOperational ? 'bg-emerald-400' : 'bg-red-400'}`} />
      {isOperational ? 'Opérationnel' : 'Problème'}
    </span>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PlatformService.getDashboard();
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (err) {
      setError('Impossible de charger les données du dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const stats = dashboardData ? [
    {
      name: 'Utilisateurs totaux',
      value: dashboardData.stats.total_users.toLocaleString(),
      subtitle: `${dashboardData.stats.active_users} actifs`,
      icon: PeopleIcon,
      color: 'accent',
    },
    {
      name: 'Utilisateurs actifs',
      value: dashboardData.stats.active_users.toLocaleString(),
      subtitle: 'Email vérifié',
      icon: CheckCircleIcon,
      color: 'success',
    },
    {
      name: 'En attente',
      value: dashboardData.stats.pending_users.toLocaleString(),
      subtitle: 'Email non vérifié',
      icon: PendingIcon,
      color: 'warning',
    },
    {
      name: 'Boutiques',
      value: dashboardData.stats.total_shops.toLocaleString(),
      subtitle: 'Sur la plateforme',
      icon: StorefrontIcon,
      color: 'info',
    },
  ] : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-surface-400 mt-1">Vue d'ensemble de la plateforme</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={fetchDashboard}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button className="btn-primary">
            + Nouvelle action
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading && !dashboardData ? (
          // Skeleton loading
          [...Array(4)].map((_, i) => (
            <div key={i} className="stat-card animate-pulse">
              <div className="h-12 w-12 bg-surface-700 rounded-xl" />
              <div className="mt-4 space-y-2">
                <div className="h-8 w-20 bg-surface-700 rounded" />
                <div className="h-4 w-32 bg-surface-700 rounded" />
              </div>
            </div>
          ))
        ) : (
          stats.map((stat, index) => (
            <StatCard key={index} stat={stat} loading={loading} />
          ))
        )}
      </div>

      {/* System Status */}
      {dashboardData && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">État du système</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50">
              <span className="text-surface-300">API</span>
              <SystemStatusBadge status={dashboardData.system_status.api} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50">
              <span className="text-surface-300">Base de données</span>
              <SystemStatusBadge status={dashboardData.system_status.database} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50">
              <span className="text-surface-300">Cache</span>
              <SystemStatusBadge status={dashboardData.system_status.cache} />
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Activité récente</h2>
            <button className="text-sm text-accent-400 hover:text-accent-300 transition-colors">
              Voir tout →
            </button>
          </div>
          
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div 
                key={activity.id} 
                className="flex items-center gap-4 p-3 rounded-lg hover:bg-surface-800/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-surface-800 flex items-center justify-center">
                  <span className="text-sm font-medium text-surface-300">
                    {activity.user.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-surface-100">
                    <span className="font-medium">{activity.user}</span>
                    {' '}{activity.action}
                  </p>
                  <p className="text-xs text-surface-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Shops */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Top Boutiques</h2>
            <button className="text-sm text-accent-400 hover:text-accent-300 transition-colors">
              Voir tout →
            </button>
          </div>
          
          <div className="space-y-4">
            {topShops.map((shop, index) => (
              <div 
                key={shop.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-800/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500/20 to-accent-700/20 border border-accent-500/30 flex items-center justify-center">
                  <span className="text-sm font-bold text-accent-400">#{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-surface-100 truncate">{shop.name}</p>
                  <p className="text-xs text-surface-500">{shop.products} produits</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono font-medium text-emerald-400">{shop.revenue}</p>
                  <p className="text-xs text-surface-500">{shop.owner}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="card-hover p-4 text-left group">
            <PeopleIcon className="w-8 h-8 text-accent-400 mb-3 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-surface-100">Gérer utilisateurs</p>
            <p className="text-xs text-surface-500 mt-1">
              {dashboardData ? `${dashboardData.stats.total_users} utilisateurs` : 'Chargement...'}
            </p>
          </button>
          
          <button className="card-hover p-4 text-left group">
            <StorefrontIcon className="w-8 h-8 text-emerald-400 mb-3 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-surface-100">Gérer boutiques</p>
            <p className="text-xs text-surface-500 mt-1">
              {dashboardData ? `${dashboardData.stats.total_shops} boutiques` : 'Chargement...'}
            </p>
          </button>
          
          <button className="card-hover p-4 text-left group">
            <svg className="w-8 h-8 text-sky-400 mb-3 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-sm font-medium text-surface-100">Rôles & Permissions</p>
            <p className="text-xs text-surface-500 mt-1">Configurer accès</p>
          </button>
          
          <button className="card-hover p-4 text-left group">
            <svg className="w-8 h-8 text-amber-400 mb-3 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm font-medium text-surface-100">Paramètres</p>
            <p className="text-xs text-surface-500 mt-1">Configuration système</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
